import {
  createFileRoute,
  Link,
  useNavigate,
  useRouter,
} from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog'
import { FaRegCalendarCheck } from 'react-icons/fa6'
import * as luxon from 'luxon'
import React, { useEffect, useMemo, useState } from 'react'
import { queryClient } from '@/lib/utils/queryUtils'
import { useOrg } from '@/lib/states/session/hooks'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { invalidateCoachOnboardingQueries } from '@/lib/utils/onboardingCache'
import { trpc } from '@/lib/trpc/client'
import { trpcUtils } from '@/lib/trpc/vanilla'
import { patchQueryCache } from '@trainrr/api-client'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  ClipboardEditIcon,
  FileText,
  Filter,
  Plus,
  Search,
} from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

export const Route = createFileRoute(
  '/_auth/_dashboard/clients/$clientId/_layout/checkin/',
)({
  component: ClientCheckIns,
  pendingComponent: () => (
    <div className="w-full max-w-4xl mx-auto space-y-6 px-4 pb-8">
      {/* Header Skeleton */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-48" />
      </div>

      {/* Filters Skeleton */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <Skeleton className="h-10 w-[200px]" />
          <Skeleton className="h-10 w-[180px]" />
          <Skeleton className="h-10 w-[180px]" />
        </div>
      </Card>

      {/* Check-ins List Skeleton */}
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <CheckInSkeleton key={index} delay={index * 100} />
        ))}
      </div>
    </div>
  ),
  loader: ({ params }) =>
    trpcUtils.checkins.listForClient.ensureData({
      clientId: params.clientId,
    }),
})

const CheckInSkeleton = ({ delay = 0 }: { delay?: number }) => (
  <Card
    className="animate-in fade-in-0"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="p-6 flex items-center gap-4">
      {/* Icon Skeleton */}
      <div className="flex-shrink-0">
        <Skeleton className="w-12 h-12 rounded-full" />
      </div>

      {/* Content Skeleton */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>

          {/* Status Badge Skeleton */}
          <div className="flex-shrink-0">
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        </div>
      </div>

      {/* Arrow Skeleton */}
      <div className="flex-shrink-0">
        <Skeleton className="w-6 h-6 rounded-full" />
      </div>
    </div>
  </Card>
)

const CHECK_IN_DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

function ClientCheckIns() {
  const { clientId } = Route.useParams()
  const { data: forms } = trpc.checkins.listForClient.useQuery(
    { clientId },
    { enabled: Boolean(clientId) },
  )
  const org = useOrg()
  const { mutateAsync: completeOnboardingItem } =
    trpc.onboarding.completeItem.useMutation()

  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState<string>('newest')

  const { data: assignmentData } = trpc.checkins.getAssignments.useQuery(
    { clientId },
    { enabled: Boolean(clientId) },
  )

  const checkInDayName =
    assignmentData?.check_in_day != null
      ? CHECK_IN_DAYS[assignmentData.check_in_day]
      : null
  const checkInTimezone = assignmentData?.timezone ?? 'UTC'
  const nextCheckInInfo = useMemo(() => {
    if (assignmentData?.check_in_day == null) {
      return null
    }
    const now = luxon.DateTime.now().setZone(checkInTimezone)
    const targetDow =
      assignmentData.check_in_day === 0 ? 7 : assignmentData.check_in_day
    let daysUntil = targetDow - now.weekday
    if (daysUntil < 0) {
      daysUntil += 7
    }
    const dayLabel =
      daysUntil === 0
        ? 'today'
        : `in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`
    return { daysUntil, dayLabel }
  }, [assignmentData?.check_in_day, checkInTimezone])

  const assignedForms = useMemo<Form[]>(() => {
    return (
      assignmentData?.assignments
        ?.filter(
          (form) =>
            form.check_in_type === 'weekly' || form.check_in_type === 'daily',
        )
        .map((form) => ({
          form_id: form.form_id,
          name: form.form.name ?? 'Check-in',
          assign_date: form.assign_date,
          check_in_type: (form.check_in_type ?? 'weekly') as 'weekly' | 'daily',
        })) ?? []
    )
  }, [assignmentData?.assignments])
  const checkInTypeByFormId = useMemo(() => {
    return assignedForms.reduce<Record<string, 'weekly' | 'daily'>>(
      (acc, form) => {
        acc[form.form_id] = form.check_in_type ?? 'weekly'
        return acc
      },
      {},
    )
  }, [assignedForms])

  // Get unique form types for filter
  const uniqueFormTypes = useMemo(() => {
    const types = new Set<string>()
    forms?.forEach((form: any) => {
      if (form.form?.name) {
        types.add(form.form.name)
      }
    })
    return Array.from(types).sort()
  }, [forms])

  const hasCheckInResponses = useMemo(() => {
    return (forms ?? []).some((form: any) => form.form?.type === 'check-in')
  }, [forms])

  // Filter and sort forms
  const filteredAndSortedForms = useMemo(() => {
    let filtered = forms || []

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter((form: any) => form.form?.name === typeFilter)
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((form: any) => {
        if (statusFilter === 'reviewed') return form.reviewed
        if (statusFilter === 'pending') {
          const checkInType =
            checkInTypeByFormId[form.form?.id ?? ''] ?? 'weekly'
          return checkInType === 'weekly' && !form.reviewed
        }
        return true
      })
    }

    // Sort
    filtered.sort((a: any, b: any) => {
      const dateA = luxon.DateTime.fromISO(a.response_date)
      const dateB = luxon.DateTime.fromISO(b.response_date)

      if (sortBy === 'newest') {
        return dateB.toMillis() - dateA.toMillis()
      } else if (sortBy === 'oldest') {
        return dateA.toMillis() - dateB.toMillis()
      }
      return 0
    })

    return filtered
  }, [forms, typeFilter, statusFilter, sortBy, checkInTypeByFormId])

  useEffect(() => {
    if (!assignModalOpen || !org?.id) {
      return
    }

    const markCheckinsManaged = async () => {
      try {
        await completeOnboardingItem({
          key: 'coach_onboarding',
          version: 1,
          itemKey: 'setup.checkins.manage',
          source: 'auto',
          metadata: { source: 'manage-checkin-dialog-open' },
        })
        await invalidateCoachOnboardingQueries(queryClient)
      } catch (error) {
        console.warn(
          'Failed to complete onboarding item setup.checkins.manage',
          error,
        )
      }
    }

    void markCheckinsManaged()
  }, [assignModalOpen, completeOnboardingItem, org?.id, queryClient])

  return (
    <>
      <AssignCheckinDialog
        onOpenChange={setAssignModalOpen}
        open={assignModalOpen}
        clientId={clientId}
        hasExistingForms={assignedForms && assignedForms.length > 0}
        assignedForms={assignedForms}
        checkInDayName={checkInDayName}
        checkInDayValue={assignmentData?.check_in_day ?? null}
      />
      <div className="ph-no-capture w-full max-w-4xl mx-auto space-y-6 px-4 pb-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Check-ins</h1>
            <p className="text-muted-foreground">
              Review your client&apos;s check-in and onboarding submissions
            </p>
            {checkInDayName ? (
              <p className="text-sm text-muted-foreground mt-1">
                Check-in day: {checkInDayName}
                {nextCheckInInfo?.dayLabel
                  ? ` · Next check-in ${nextCheckInInfo.dayLabel}`
                  : ''}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground mt-1">
                Check-in day not set
              </p>
            )}
          </div>
          <Button
            onClick={() => setAssignModalOpen(true)}
            className="w-full sm:w-auto"
          >
            {assignedForms && assignedForms.length > 0
              ? 'Manage Check-In Forms'
              : 'Assign Check-In Form'}
          </Button>
        </div>

        {/* Filters */}
        {forms && forms.length > 0 && (
          <Card className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Type Filter */}
              {uniqueFormTypes.length > 1 && (
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-full sm:w-[200px]">
                    <FileText className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {uniqueFormTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                </SelectContent>
              </Select>

              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </Card>
        )}

        {/* Responses List */}
        <div className="space-y-3">
          {filteredAndSortedForms.length > 0
            ? filteredAndSortedForms.map((response) => (
                <FormResponseRow
                  key={response.id}
                  response={response}
                  checkInType={checkInTypeByFormId[response.form?.id ?? '']}
                  clientId={clientId ?? ''}
                />
              ))
            : null}

          {!hasCheckInResponses && (
            <Card className="p-12">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                  <FaRegCalendarCheck className="w-8 h-8 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">No check-ins yet</h3>
                  <p className="text-muted-foreground max-w-sm">
                    This client hasn&apos;t submitted any check-ins yet. Once
                    they do, their submissions will appear here.
                  </p>
                </div>
                <Button
                  onClick={() => setAssignModalOpen(true)}
                  variant="outline"
                >
                  Assign First Check-In
                </Button>
              </div>
            </Card>
          )}
          {filteredAndSortedForms.length === 0 &&
          hasCheckInResponses &&
          forms &&
          forms.length > 0 ? (
            <Card className="p-8">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <Search className="w-6 h-6 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">No results found</h3>
                  <p className="text-muted-foreground">
                    Try adjusting your search or filter criteria.
                  </p>
                </div>
              </div>
            </Card>
          ) : null}
        </div>
      </div>
    </>
  )
}

const FormResponseRow: React.FC<{
  response: any
  checkInType?: 'weekly' | 'daily'
  clientId: string
}> = ({ response, checkInType, clientId }) => {
  const isOnboarding = response.form?.type === 'onboarding'
  const responseDate = luxon.DateTime.fromISO(response.response_date)
  const isToday = responseDate.hasSame(luxon.DateTime.now(), 'day')
  const isYesterday = responseDate.hasSame(
    luxon.DateTime.now().minus({ days: 1 }),
    'day',
  )

  const formatMainDate = () => {
    if (isToday) {
      return 'Today'
    } else if (isYesterday) {
      return 'Yesterday'
    } else {
      return responseDate.toFormat('MMM d, yyyy')
    }
  }

  const formatSubDate = () => {
    if (isToday || isYesterday) {
      return responseDate.toFormat('h:mm a')
    } else {
      return responseDate.toFormat('h:mm a')
    }
  }

  const getStatusBadge = () => {
    if (response.reviewed) {
      return (
        <Badge
          variant="default"
          className="shrink-0 whitespace-nowrap bg-blue-100 text-blue-800 hover:bg-blue-200 [.dark_&]:bg-blue-950 [.dark_&]:text-blue-300 [.dark_&]:hover:bg-blue-900"
        >
          Reviewed
        </Badge>
      )
    }
    if (isOnboarding) {
      return (
        <Badge
          variant="secondary"
          className="shrink-0 whitespace-nowrap bg-emerald-100 text-emerald-800 hover:bg-emerald-200 [.dark_&]:bg-emerald-950 [.dark_&]:text-emerald-300 [.dark_&]:hover:bg-emerald-900"
        >
          Submitted
        </Badge>
      )
    }
    if (checkInType === 'weekly') {
      return (
        <Badge
          variant="secondary"
          className="shrink-0 whitespace-nowrap bg-orange-100 text-orange-800 hover:bg-orange-200 [.dark_&]:bg-orange-950 [.dark_&]:text-orange-300 [.dark_&]:hover:bg-orange-900"
        >
          Pending
        </Badge>
      )
    }
    return null
  }

  const formName = response.form?.name as string | undefined
  const typeLabel = isOnboarding ? 'Onboarding' : 'Check-in'
  const subtitle = [
    formName,
    formName && formName.toLowerCase() === typeLabel.toLowerCase()
      ? null
      : typeLabel,
    formatSubDate(),
  ]
    .filter(Boolean)
    .join(' • ')

  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <Link
        to="/clients/$clientId/checkin/$formId/$responseId"
        params={{
          clientId,
          formId: response.form.id,
          responseId: response.id,
        }}
      >
        <div className="flex items-center gap-3 p-3 transition-colors duration-200 hover:bg-muted/50 sm:gap-4 sm:p-6">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full sm:h-12 sm:w-12 ${
              isOnboarding || response.reviewed || checkInType !== 'weekly'
                ? 'bg-primary/10'
                : 'bg-orange-100'
            }`}
          >
            {isOnboarding ? (
              <ClipboardEditIcon className="h-5 w-5 text-primary sm:h-6 sm:w-6" />
            ) : (
              <FaRegCalendarCheck
                className={`h-5 w-5 sm:h-6 sm:w-6 ${
                  response.reviewed || checkInType !== 'weekly'
                    ? 'text-primary'
                    : 'text-orange-600'
                }`}
              />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="whitespace-nowrap text-base font-semibold text-foreground sm:text-lg">
                {formatMainDate()}
              </h3>
              {getStatusBadge()}
            </div>
            <p className="mt-0.5 truncate text-sm text-muted-foreground">
              {subtitle}
            </p>
          </div>

          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted">
            <svg
              className="h-4 w-4 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </Link>
    </Card>
  )
}

type Form = {
  form_id: string
  name: string
  assign_date: string
  check_in_type: 'weekly' | 'daily'
}

const AssignCheckinDialog: React.FC<{
  open: boolean
  onOpenChange: (open: boolean) => void
  clientId?: string
  hasExistingForms?: boolean
  assignedForms?: any[]
  checkInDayName?: string | null
  checkInDayValue?: number | null
}> = ({
  open,
  onOpenChange,
  clientId,
  hasExistingForms,
  assignedForms,
  checkInDayName,
  checkInDayValue,
}) => {
  const org = useOrg()
  const navigate = useNavigate()
  const router = useRouter()
  const utils = trpc.useUtils()
  const assignToClient = trpc.checkins.assignToClient.useMutation()

  const [weeklyForm, setWeeklyForm] = useState<Form | null>(null)
  const [dailyForm, setDailyForm] = useState<Form | null>(null)
  const [selectedCheckInDay, setSelectedCheckInDay] = useState<number | null>(
    null,
  )

  const { data: formOptions, error } = trpc.forms.listOptions.useQuery(
    { type: 'check-in' },
    { enabled: Boolean(org?.id) },
  )
  if (error) {
    console.error(error)
  }

  const existingWeeklyForm = useMemo(
    () =>
      assignedForms?.find((form) => form.check_in_type === 'weekly') ?? null,
    [assignedForms],
  )
  const existingDailyForm = useMemo(
    () => assignedForms?.find((form) => form.check_in_type === 'daily') ?? null,
    [assignedForms],
  )

  // Get assigned forms for this client
  useEffect(() => {
    if (!assignedForms) return
    setWeeklyForm(existingWeeklyForm)
    setDailyForm(existingDailyForm)
  }, [assignedForms, existingWeeklyForm, existingDailyForm])

  useEffect(() => {
    setSelectedCheckInDay(checkInDayValue ?? null)
  }, [checkInDayValue])

  const handleAssign = async () => {
    if (!clientId) {
      return
    }

    try {
      await assignToClient.mutateAsync({
        clientId,
        weeklyFormId: weeklyForm?.form_id ?? null,
        dailyFormId: dailyForm?.form_id ?? null,
        checkInDay: selectedCheckInDay,
      })
    } catch (error) {
      console.error(error, 'Error assigning form')
      toast.error('Error assigning form')
      return
    }

    const assignDate = new Date().toISOString()
    patchQueryCache(utils.checkins.getAssignments, { clientId }, (old) => {
      const nextAssignments = []
      if (weeklyForm?.form_id) {
        const existing = old.assignments.find(
          (row) =>
            row.form_id === weeklyForm.form_id &&
            row.check_in_type === 'weekly',
        )
        nextAssignments.push({
          form_id: weeklyForm.form_id,
          client_id: clientId,
          assign_date: existing?.assign_date ?? assignDate,
          check_in_type: 'weekly' as const,
          next_date: existing?.next_date ?? null,
          reminder_days: existing?.reminder_days ?? null,
          reminder_time: existing?.reminder_time ?? null,
          form: { id: weeklyForm.form_id, name: weeklyForm.name },
        })
      }
      if (dailyForm?.form_id) {
        const existing = old.assignments.find(
          (row) =>
            row.form_id === dailyForm.form_id && row.check_in_type === 'daily',
        )
        nextAssignments.push({
          form_id: dailyForm.form_id,
          client_id: clientId,
          assign_date: existing?.assign_date ?? assignDate,
          check_in_type: 'daily' as const,
          next_date: existing?.next_date ?? null,
          reminder_days: existing?.reminder_days ?? [0, 1, 2, 3, 4, 5, 6],
          reminder_time: existing?.reminder_time ?? null,
          form: { id: dailyForm.form_id, name: dailyForm.name },
        })
      }
      return {
        ...old,
        check_in_day: selectedCheckInDay,
        assignments: nextAssignments,
      }
    })
    await router.invalidate()

    toast.success('Check-ins updated')
    onOpenChange(false)
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        analyticsName="client_checkin_assignment_dialog"
      >
        <DialogContent className="ph-no-capture max-w-[560px] max-h-[90vh] flex flex-col p-0 w-[95vw] sm:w-full">
          {/* Fixed Header */}
          <div className="flex-shrink-0 p-4 sm:p-6 pb-4 border-b">
            <DialogHeader className="space-y-2">
              <h2 className="text-2xl font-bold tracking-tight">
                {hasExistingForms
                  ? 'Manage Check-In Forms'
                  : 'Assign Check-Ins'}
              </h2>
              <p className="text-muted-foreground">
                {hasExistingForms
                  ? 'Update the weekly and daily check-in forms for this client'
                  : 'Assign weekly and daily check-in forms for this client'}
              </p>
            </DialogHeader>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 sm:p-6 space-y-4">
              <Card className="p-4 sm:p-6">
                <div className="flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                        <CalendarDays className="h-5 w-5 text-muted-foreground" />
                        Check-in day
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Reminder to check in is sent at 9am on this day.
                      </p>
                    </div>
                  </div>
                  <Select
                    value={
                      selectedCheckInDay !== null
                        ? String(selectedCheckInDay)
                        : 'none'
                    }
                    onValueChange={(value) => {
                      setSelectedCheckInDay(
                        value === 'none' ? null : Number(value),
                      )
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a day" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No check-in day</SelectItem>
                      <SelectItem value="0">Sunday</SelectItem>
                      <SelectItem value="1">Monday</SelectItem>
                      <SelectItem value="2">Tuesday</SelectItem>
                      <SelectItem value="3">Wednesday</SelectItem>
                      <SelectItem value="4">Thursday</SelectItem>
                      <SelectItem value="5">Friday</SelectItem>
                      <SelectItem value="6">Saturday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </Card>

              <Card className="p-4 sm:p-6">
                <div className="flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                        <CalendarCheck className="h-5 w-5 text-muted-foreground" />
                        Weekly check-in
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Reminder to check in is sent at 9am on the client&apos;s
                        check-in day.
                      </p>
                      {checkInDayName && (
                        <p className="text-sm text-muted-foreground mt-2">
                          Current check-in day: {checkInDayName}
                        </p>
                      )}
                      {weeklyForm?.assign_date && (
                        <p className="text-sm text-muted-foreground mt-2">
                          Assigned on{' '}
                          {luxon.DateTime.fromISO(
                            weeklyForm.assign_date,
                          ).toLocaleString(luxon.DateTime.DATETIME_MED)}
                        </p>
                      )}
                    </div>
                    <div className="w-9 h-9" aria-hidden="true" />
                  </div>
                  <Select
                    value={weeklyForm?.form_id ?? 'none'}
                    onValueChange={(value) => {
                      if (value === 'none') {
                        setWeeklyForm(null)
                        return
                      }
                      if (value === 'create-new') {
                        navigate({ to: '/forms' })
                        return
                      }
                      const selected = formOptions?.find((form) => form.id === value)
                      if (!selected) return
                      const assignDate =
                        existingWeeklyForm?.form_id === selected.id
                          ? existingWeeklyForm.assign_date
                          : ''
                      setWeeklyForm({
                        form_id: selected.id,
                        name: selected.name,
                        assign_date: assignDate,
                        check_in_type: 'weekly',
                      })
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select weekly check-in form" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No weekly check-in</SelectItem>
                      {formOptions?.map((form) => (
                        <SelectItem key={form.id} value={form.id}>
                          {form.name}
                        </SelectItem>
                      ))}
                      <SelectSeparator />
                      <SelectItem value="create-new">
                        <span className="inline-flex items-center gap-2">
                          <Plus className="h-4 w-4" />
                          Create new form
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </Card>

              <Card className="p-4 sm:p-6">
                <div className="flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                        <CalendarClock className="h-5 w-5 text-muted-foreground" />
                        Daily check-in
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Reminder to check in is sent every day at 9am.
                      </p>
                      {dailyForm?.assign_date && (
                        <p className="text-sm text-muted-foreground mt-2">
                          Assigned on{' '}
                          {luxon.DateTime.fromISO(
                            dailyForm.assign_date,
                          ).toLocaleString(luxon.DateTime.DATETIME_MED)}
                        </p>
                      )}
                    </div>
                    <div className="w-9 h-9" aria-hidden="true" />
                  </div>
                  <Select
                    value={dailyForm?.form_id ?? 'none'}
                    onValueChange={(value) => {
                      if (value === 'none') {
                        setDailyForm(null)
                        return
                      }
                      if (value === 'create-new') {
                        navigate({ to: '/forms' })
                        return
                      }
                      const selected = formOptions?.find((form) => form.id === value)
                      if (!selected) return
                      const assignDate =
                        existingDailyForm?.form_id === selected.id
                          ? existingDailyForm.assign_date
                          : ''
                      setDailyForm({
                        form_id: selected.id,
                        name: selected.name,
                        assign_date: assignDate,
                        check_in_type: 'daily',
                      })
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select daily check-in form" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No daily check-in</SelectItem>
                      {formOptions?.map((form) => (
                        <SelectItem key={form.id} value={form.id}>
                          {form.name}
                        </SelectItem>
                      ))}
                      <SelectSeparator />
                      <SelectItem value="create-new">
                        <span className="inline-flex items-center gap-2">
                          <Plus className="h-4 w-4" />
                          Create new form
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </Card>
            </div>
          </div>

          {/* Fixed Footer */}
          <div className="flex-shrink-0 p-4 sm:p-6 pt-4 border-t bg-background">
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssign}
                className="w-full sm:w-auto"
                disabled={
                  !weeklyForm &&
                  !dailyForm &&
                  !existingWeeklyForm &&
                  !existingDailyForm
                }
              >
                {hasExistingForms ? 'Update Forms' : 'Assign Forms'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
